import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Power, PowerOff, Copy, Check, Loader, BookOpen, ExternalLink } from 'lucide-react';
import apiClient from '../api/client';

const ApiKeyManagement = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  
  const [newKey, setNewKey] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/api-keys');
      setApiKeys(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar chaves API');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    
    if (!newKey.name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      
      const response = await apiClient.post('/admin/api-keys', newKey);
      
      // Show the full key immediately (only time it will be shown)
      const createdKey = response.data.data;
      setApiKeys([createdKey, ...apiKeys]);
      
      // Reset form
      setNewKey({ name: '', description: '' });
      setShowCreateForm(false);
      
      // Auto-copy the new key
      copyToClipboard(createdKey.key_value);
      
      alert('✅ Chave API criada com sucesso!\n\nA chave foi copiada para a área de transferência.\nGuarde-a em local seguro - ela não será exibida novamente.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar chave API');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      const endpoint = currentStatus ? 'deactivate' : 'activate';
      await apiClient.patch(`/admin/api-keys/${id}/${endpoint}`);
      await loadApiKeys();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar chave API');
    }
  };

  const handleDeleteKey = async (id, name) => {
    if (!window.confirm(`Tem certeza que deseja excluir a chave "${name}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await apiClient.delete(`/admin/api-keys/${id}`);
      await loadApiKeys();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir chave API');
    }
  };

  const copyToClipboard = (text) => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopiedKey(text);
          setTimeout(() => setCopiedKey(null), 2000);
        })
        .catch(() => {
          // Fallback to older method
          fallbackCopyToClipboard(text);
        });
    } else {
      // Use fallback for non-HTTPS contexts
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopiedKey(text);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Não foi possível copiar automaticamente. Por favor, copie manualmente:\n\n' + text);
    }
    
    document.body.removeChild(textArea);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gerenciamento de Chaves API</h2>
          <p className="mt-1 text-sm text-gray-400">
            Gerencie chaves de acesso para serviços externos
          </p>
        </div>
        <div className="flex space-x-3">
          <a
            href="/api-docs"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 border border-dark-500 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-dark-700 hover:bg-dark-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <BookOpen className="h-5 w-5 mr-2" />
            Documentação da API
            <ExternalLink className="h-4 w-4 ml-2" />
          </a>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nova Chave API
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900 bg-opacity-20 border border-red-500 rounded-md p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {showCreateForm && (
        <div className="bg-dark-700 shadow rounded-lg p-6 border border-dark-500">
          <h3 className="text-lg font-medium text-white mb-4">Criar Nova Chave API</h3>
          <form onSubmit={handleCreateKey} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                Nome do Provedor *
              </label>
              <input
                type="text"
                id="name"
                value={newKey.name}
                onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                placeholder="Ex: Sistema de Conciliação XYZ"
                className="mt-1 block w-full rounded-md border-dark-500 bg-dark-600 text-white placeholder-gray-500 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                required
              />
              <p className="mt-1 text-xs text-gray-400">
                Nome identificador do serviço que usará esta chave
              </p>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300">
                Descrição
              </label>
              <textarea
                id="description"
                value={newKey.description}
                onChange={(e) => setNewKey({ ...newKey, description: e.target.value })}
                placeholder="Ex: Chave para integração com sistema de conciliação financeira"
                rows={3}
                className="mt-1 block w-full rounded-md border-dark-500 bg-dark-600 text-white placeholder-gray-500 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-400">
                Informações adicionais sobre o uso desta chave
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewKey({ name: '', description: '' });
                  setError(null);
                }}
                className="px-4 py-2 border border-dark-500 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-dark-600 hover:bg-dark-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={creating}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {creating ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4 mr-2" />
                    Criar Chave
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-dark-700 shadow rounded-lg overflow-hidden border border-dark-500">
        <table className="min-w-full divide-y divide-dark-500">
          <thead className="bg-dark-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Provedor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Chave API
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Último Uso
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Criada Em
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-dark-700 divide-y divide-dark-500">
            {apiKeys.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-400">
                  <Key className="h-12 w-12 mx-auto text-gray-500 mb-3" />
                  <p>Nenhuma chave API criada ainda</p>
                  <p className="text-xs mt-1">Clique em "Nova Chave API" para criar uma</p>
                </td>
              </tr>
            ) : (
              apiKeys.map((key) => (
                <tr key={key.id} className={!key.is_active ? 'bg-dark-800 bg-opacity-50' : ''}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-white">{key.name}</div>
                      {key.description && (
                        <div className="text-xs text-gray-400 mt-1">{key.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <code className="text-xs bg-dark-800 text-gray-300 px-2 py-1 rounded font-mono">
                        {key.key_value_full || key.key_value}
                      </code>
                      <button
                        onClick={() => copyToClipboard(key.key_value_full || key.key_value)}
                        className="text-gray-400 hover:text-gray-300"
                        title="Copiar chave"
                      >
                        {copiedKey === (key.key_value_full || key.key_value) ? (
                          <Check className="h-4 w-4 text-primary-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        key.is_active
                          ? 'bg-primary-500 bg-opacity-20 text-primary-500'
                          : 'bg-gray-500 bg-opacity-20 text-gray-400'
                      }`}
                    >
                      {key.is_active ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-400">
                    {formatDate(key.last_used_at)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-400">
                    {formatDate(key.created_at)}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleToggleActive(key.id, key.is_active)}
                      className={`inline-flex items-center px-3 py-1 border border-transparent text-xs rounded-md ${
                        key.is_active
                          ? 'text-yellow-400 bg-yellow-500 bg-opacity-20 hover:bg-opacity-30'
                          : 'text-primary-500 bg-primary-500 bg-opacity-20 hover:bg-opacity-30'
                      }`}
                      title={key.is_active ? 'Desativar' : 'Ativar'}
                    >
                      {key.is_active ? (
                        <>
                          <PowerOff className="h-3 w-3 mr-1" />
                          Desativar
                        </>
                      ) : (
                        <>
                          <Power className="h-3 w-3 mr-1" />
                          Ativar
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteKey(key.id, key.name)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs rounded-md text-red-400 bg-red-500 bg-opacity-20 hover:bg-opacity-30"
                      title="Excluir"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Excluir
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-blue-500 bg-opacity-10 border border-blue-500 border-opacity-30 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-400 mb-2">ℹ️ Informações Importantes</h4>
        <ul className="text-sm text-blue-300 space-y-1 list-disc list-inside">
          <li>Chaves API são exibidas completas apenas no momento da criação</li>
          <li>Guarde as chaves em local seguro - elas não podem ser recuperadas depois</li>
          <li>Desative chaves não utilizadas para maior segurança</li>
          <li>O campo "Último Uso" ajuda a identificar chaves inativas</li>
          <li>Chaves desativadas não podem ser usadas para autenticação</li>
        </ul>
      </div>

      <div className="bg-dark-700 border border-dark-500 rounded-md p-4">
        <h4 className="text-sm font-medium text-white mb-3 flex items-center">
          <BookOpen className="h-4 w-4 mr-2" />
          Como Usar sua Chave API
        </h4>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-gray-400 mb-2">Inclua sua chave no header X-API-Key de todas as requisições:</p>
            <pre className="bg-dark-800 text-gray-300 p-3 rounded text-xs overflow-x-auto">
{`curl -H "X-API-Key: pk_live_sua_chave_aqui" \\
     https://links.papello.dev.br/api/payment-plans`}
            </pre>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-dark-500">
            <p className="text-xs text-gray-400">
              Para exemplos completos e documentação detalhada:
            </p>
            <a
              href="/api-docs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs font-medium text-primary-500 hover:text-primary-400"
            >
              Ver Documentação Completa
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyManagement;
